"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { consciousnessStore } from "@/lib/mindaloo/consciousnessStore";

export default function MindalooStarfield() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x02030a, 1);
    mount.appendChild(renderer.domElement);

    // ── Scene & Camera ───────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 2000);
    camera.position.set(0, 0, 300);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    // ── Layer 1: Distant tiny stars (2000) ───────────────────────────────────
    const tinyCount = 2000;
    const tinyGeo = new THREE.BufferGeometry();
    const tinyPos = new Float32Array(tinyCount * 3);
    for (let i = 0; i < tinyCount; i++) {
      tinyPos[i * 3]     = rand(-900, 900);
      tinyPos[i * 3 + 1] = rand(-600, 600);
      tinyPos[i * 3 + 2] = rand(-800, -100);
    }
    tinyGeo.setAttribute("position", new THREE.BufferAttribute(tinyPos, 3));
    const tinyMat = new THREE.PointsMaterial({
      color: 0xd0d8ff,
      size: 0.55,
      transparent: true,
      opacity: 0.72,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(tinyGeo, tinyMat));

    // ── Layer 2: Mid stars (800) ─────────────────────────────────────────────
    const midCount = 800;
    const midGeo = new THREE.BufferGeometry();
    const midPos = new Float32Array(midCount * 3);
    const midColors = new Float32Array(midCount * 3);
    for (let i = 0; i < midCount; i++) {
      midPos[i * 3]     = rand(-700, 700);
      midPos[i * 3 + 1] = rand(-450, 450);
      midPos[i * 3 + 2] = rand(-400, 100);
      // blue-white to warm white
      const t = Math.random();
      midColors[i * 3]     = 0.78 + t * 0.22;
      midColors[i * 3 + 1] = 0.82 + t * 0.18;
      midColors[i * 3 + 2] = 1.0;
    }
    midGeo.setAttribute("position", new THREE.BufferAttribute(midPos, 3));
    midGeo.setAttribute("color",    new THREE.BufferAttribute(midColors, 3));
    const midMat = new THREE.PointsMaterial({
      vertexColors: true,
      size: 1.2,
      transparent: true,
      opacity: 0.85,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(midGeo, midMat));

    // ── Layer 3: Bright foreground stars (120) ────────────────────────────────
    const brightCount = 120;
    const brightGeo = new THREE.BufferGeometry();
    const brightPos = new Float32Array(brightCount * 3);
    for (let i = 0; i < brightCount; i++) {
      brightPos[i * 3]     = rand(-500, 500);
      brightPos[i * 3 + 1] = rand(-350, 350);
      brightPos[i * 3 + 2] = rand(-100, 200);
    }
    brightGeo.setAttribute("position", new THREE.BufferAttribute(brightPos, 3));
    const brightMat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2.2,
      transparent: true,
      opacity: 0.95,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(brightGeo, brightMat));


    // ── Aurora glow sprites (soft radial gradient — no hard edges) ───────────
    const makeAuroraTexture = (colorStop: string, colorStop2: string) => {
      const size = 512;
      const cvs = document.createElement("canvas");
      cvs.width = size * 6; cvs.height = size;
      const ctx = cvs.getContext("2d")!;
      // Horizontal: very long fade — transparent for 25% each side
      const gx = ctx.createLinearGradient(0, 0, size * 6, 0);
      gx.addColorStop(0,    "transparent");
      gx.addColorStop(0.08, "transparent");
      gx.addColorStop(0.25, colorStop);
      gx.addColorStop(0.5,  colorStop2);
      gx.addColorStop(0.75, colorStop);
      gx.addColorStop(0.92, "transparent");
      gx.addColorStop(1,    "transparent");
      // Vertical: very tight bell — opaque only in centre 20%
      const gy = ctx.createLinearGradient(0, 0, 0, size);
      gy.addColorStop(0,    "transparent");
      gy.addColorStop(0.35, "transparent");
      gy.addColorStop(0.45, "rgba(255,255,255,1)");
      gy.addColorStop(0.55, "rgba(255,255,255,1)");
      gy.addColorStop(0.65, "transparent");
      gy.addColorStop(1,    "transparent");
      ctx.fillStyle = gx;
      ctx.fillRect(0, 0, size * 6, size);
      ctx.globalCompositeOperation = "destination-in";
      ctx.fillStyle = gy;
      ctx.fillRect(0, 0, size * 6, size);
      return new THREE.CanvasTexture(cvs);
    };

    const auroraTex  = makeAuroraTexture("rgba(74,31,168,0.55)",  "rgba(100,50,220,0.70)");
    const aurora2Tex = makeAuroraTexture("rgba(13,110,90,0.40)",  "rgba(20,160,120,0.55)");

    const makeAuroraSprite = (tex: THREE.CanvasTexture) => {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.scale.set(1600, 80, 1);
      return sprite;
    };

    const aurora  = makeAuroraSprite(auroraTex);
    aurora.position.set(0, -65, -380);  // horizon at tagline level
    aurora.scale.set(2200, 36, 1);       // very wide, very thin
    scene.add(aurora);

    const aurora2 = makeAuroraSprite(aurora2Tex);
    aurora2.position.set(0, -75, -360);
    aurora2.scale.set(1600, 20, 1);      // whisper echo layer
    scene.add(aurora2);

    const auroraMat  = aurora.material  as THREE.SpriteMaterial;
    const aurora2Mat = aurora2.material as THREE.SpriteMaterial;

    // ── C-score ref (updated by store subscription) ───────────────────────────
    const cScore = { value: consciousnessStore.score };
    const unsubStore = consciousnessStore.subscribe(s => { cScore.value = s; });

    // ── Resize handler ───────────────────────────────────────────────────────
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    // ── Animation loop ───────────────────────────────────────────────────────
    let rafId: number;
    let t = 0;

    const animate = () => {
      rafId = requestAnimationFrame(animate);

      const c = cScore.value / 100; // 0–1
      // Speed scales with consciousness — faster when elevated
      const speed = 0.0003 + c * 0.0005;
      t += speed;

      // Camera drift — orbit radius grows with C-score
      const orbitR = 12 + c * 22;
      camera.position.x = Math.sin(t * 0.7) * orbitR;
      camera.position.y = Math.cos(t * 0.5) * (orbitR * 0.55);
      camera.position.z = 300 + Math.sin(t * 0.3) * (10 + c * 25);
      camera.lookAt(0, 0, 0);

      // Star brightness scales with C-score
      brightMat.opacity = (0.65 + c * 0.30) + Math.sin(t * 18) * (0.06 + c * 0.12);
      tinyMat.opacity   = (0.50 + c * 0.25) + Math.sin(t * 9 + 1.2) * 0.05;
      midMat.opacity    = 0.70 + c * 0.20;

      // Aurora sweeps in above C=55, peaks at C=90+
      // Sprites face camera, so just drive opacity + slow y drift for shimmer
      // Aurora always softly visible (from C=0), brightens with consciousness
      const auroraBase = 0.18 + c * 0.45;
      auroraMat.opacity  = auroraBase * (0.9 + Math.sin(t * 5) * 0.10);
      aurora2Mat.opacity = (auroraBase * 0.55) * (0.9 + Math.sin(t * 7.5 + 1.5) * 0.08);
      // Gentle drift — horizon stays near logo base
      aurora.position.y  = -65 + Math.sin(t * 0.35) * 4;
      aurora2.position.y = -75 + Math.sin(t * 0.55 + 0.8) * 3;

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      unsubStore();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="fixed inset-0"
      style={{ zIndex: 0, pointerEvents: "none" }}
      aria-hidden
    />
  );
}
